import {
  AttendeeItemContainer,
  InputForm,
  Label,
  MeetingRoomDropdownContainer,
  MeetingRoomDropdownItem,
  MemberListContainer,
  MemberListItem,
  RepeatConditionContainer,
  RepeatConditionItem,
  TimeContainer,
  TimeDropdownContainer,
  TimeDropdownItem,
} from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { Calendar } from "react-date-range";
import { ko } from "date-fns/locale";
import { TMeetingRoom } from "apis/meetings/meeting-room";
import { debounce } from "lodash";
import { TFormattedAttendee } from "apis/meetings/meeting-reservation";
import { useRecoilValue } from "recoil";
import { user } from "recoil/recoilState";
import { getMemberList } from "apis/members";
import { TMember } from "types/user.types";
import _ from "lodash";
import ToolTip from "uis/new-tootip";
import { isValidEmail } from "utils/validation";
import * as Modal from "uis/modal";
import { RepeatEndOption, RepeatRuleOption, RepeatUnit, TRRule } from "uis/modal/repeat-rule-setting-modal/types";
import { getKorDate } from "utils/date";
import RepeatRuleSettingModalService from "uis/modal/repeat-rule-setting-modal/services";
import { RepeatReservationConditionalOption } from "uis/modal/repeat-edit-conditinal-modal";
import { UtilsService } from "utils/utils.service";
import { RRuleService } from "utils/rrule.service";
import MinAttendeeLimitWarning from "./MinAttendeeLimitWarning";

//INFO: 회의실 예약 모달에서 예약 제목으로 사용되는 컴포넌트
export const Title = ({ onChange, value }: { onChange?: (value: string) => void; value?: string }) => {
  return (
    <InputForm className="title">
      <Label>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
          예약 제목
        </UI.Typography>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-primary-normal)">
          *
        </UI.Typography>
      </Label>
      <UI.Input
        placeholder="회의실 예약"
        width={"100%"}
        showMaxLength
        maxLength={100}
        value={value}
        onChange={(value) => {
          if (value.length > 100) return;
          else {
            onChange?.(value);
          }
        }}
      />
    </InputForm>
  );
};

//INFO: 회의실 예약 모달에서 예약 일자 선택으로 사용되는 컴포넌트
export const DateInput = ({ value, onChange }: { value: Date; onChange?: (value: Date) => void }) => {
  const year = value.getFullYear();
  const month = value.getMonth() + 1;
  const date = value.getDate();
  const today: Date = new Date();
  const after180Days: Date = new Date();
  after180Days.setDate(today.getDate() + 180);
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    const handler = (event: any) => {
      if (open && ref.current && !ref.current.contains(event.target)) {
        setOpen?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  return (
    <InputForm className="date">
      <Label>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
          예약 일시
        </UI.Typography>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-primary-normal)">
          *
        </UI.Typography>
      </Label>
      <div style={{ display: "flex", gap: "8px", flexDirection: "column", width: "100%" }}>
        <div
          className="calendar-selector"
          style={{ position: "relative", width: "100%" }}
          onFocus={() => setOpen(true)}
        >
          <UI.Input
            prefixIcon="calendar"
            showPrefixIcon
            value={`${year}. ${month.toString().padStart(2, "0")}. ${date.toString().padStart(2, "0")}`}
            width={"100%"}
          />
          <div
            ref={ref}
            className="calendar-modal"
            style={{
              display: open ? "block" : "none",
              position: "absolute",
              boxShadow: "var(--alias-style-shadow-strong)",
              backgroundColor: "white",
              zIndex: 100,
              width: 356,
              left: 2,
            }}
          >
            <Calendar
              locale={ko}
              date={value}
              onChange={(date) => onChange?.(date)}
              minDate={today}
              maxDate={after180Days}
            />
          </div>
        </div>
      </div>
    </InputForm>
  );
};

//INFO: 회의실 예약 모달에서 예약 시간 선택으로 사용되는 컴포넌트
export const Time = ({
  isToday,
  startValue,
  onChangeStart,
  endValue,
  onChangeEnd,
}: {
  startValue?: { hour: number; minutes: number };
  onChangeStart: (hour: number, minutes: number) => void;
  endValue?: { hour: number; minutes: number };
  onChangeEnd: (hour: number, minutes: number) => void;
  isToday?: boolean;
}) => {
  const [openStartTimeDropbox, setOpenStartTimeDropbox] = useState<boolean>(false);
  const [openEndTimeDropbox, setOpenEndTimeDropbox] = useState<boolean>(false);

  useEffect(() => {
    //INFO: 시작 시간이 종료 시간보다 늦을 경우 종료 시간을 시작 시간보다 30분 뒤로 설정
    if (startValue && endValue) {
      if (startValue.hour > endValue.hour) {
        if (startValue.minutes === 30) {
          onChangeEnd(startValue.hour + 1, 0);
        } else {
          onChangeEnd(startValue.hour, 30);
        }
      } else if (startValue.hour === endValue.hour && startValue.minutes > endValue.minutes) {
        if (startValue.minutes === 30) {
          onChangeEnd(startValue.hour + 1, 0);
        } else {
          onChangeEnd(startValue.hour, 30);
        }
      } else if (endValue.hour === startValue.hour && endValue.minutes === startValue.minutes) {
        //INFO: 시작 시간과 종료 시간이 같을 경우 종료 시간을 30분 뒤로 설정
        onChangeEnd(
          startValue.minutes === 30 ? startValue?.hour + 1 : startValue?.hour,
          startValue?.minutes === 30 ? 0 : 30
        );
      }
    }
  }, [startValue]);

  //INFO: 최소 종료시간 설정
  const getMinTime = () => {
    let newHour = startValue?.hour ?? 7;
    let newMinutes = (startValue?.minutes ?? 0) + 30;

    if (newMinutes >= 60) {
      newMinutes -= 60;
      newHour += 1;
    }

    let minTime = { hour: newHour, minutes: newMinutes };
    if (isToday) {
      const today = new Date();
      if (minTime.hour === today.getHours() && minTime.minutes <= today.getMinutes()) {
        minTime = {
          hour: today.getMinutes() > 30 ? today.getHours() + 1 : today.getHours(),
          minutes: today.getMinutes() === 0 ? 0 : today.getMinutes() < 30 ? 30 : 0,
        };
      } else if (minTime.hour < today.getHours()) {
        minTime = {
          hour: today.getMinutes() > 30 ? today.getHours() + 1 : today.getHours(),
          minutes: today.getMinutes() === 0 ? 0 : today.getMinutes() < 30 ? 30 : 0,
        };
      }
    }

    return minTime;
  };

  return (
    <TimeContainer>
      <InputForm className="start-time">
        <div style={{ position: "relative" }} onFocus={() => setOpenStartTimeDropbox(true)}>
          <UI.Input
            prefixIcon="clock"
            showPrefixIcon
            width={"100%"}
            value={`${startValue?.hour.toString().padStart(2, "0")}:${startValue?.minutes.toString().padStart(2, "0")}`}
          />
          <TimeDropdown
            open={openStartTimeDropbox}
            setOpen={setOpenStartTimeDropbox}
            value={{ hour: startValue?.hour ?? 7, minutes: startValue?.minutes ?? 0 }}
            onChange={(hour, minutes) => {
              onChangeStart(hour, minutes);
              setOpenStartTimeDropbox(false);
            }}
            maxTime={{ hour: 22, minutes: 30 }}
          />
        </div>
      </InputForm>
      <div
        style={{
          width: "12px",
          height: 1,
          marginBottom: 24,
          backgroundColor: "var(--alias-color-label-normal)",
        }}
      />
      <InputForm className="end-time">
        <div style={{ position: "relative" }} onFocus={() => setOpenEndTimeDropbox(true)}>
          <UI.Input
            prefixIcon="clock"
            showPrefixIcon
            width={"100%"}
            value={`${endValue?.hour.toString().padStart(2, "0")}:${endValue?.minutes.toString().padStart(2, "0")}`}
          />
          <TimeDropdown
            minTime={{ hour: getMinTime().hour ?? 7, minutes: getMinTime().minutes ?? 30 }}
            open={openEndTimeDropbox}
            setOpen={setOpenEndTimeDropbox}
            value={{ hour: endValue?.hour ?? 7, minutes: endValue?.minutes ?? 0 }}
            onChange={(hour, minutes) => {
              onChangeEnd(hour, minutes);
              setOpenEndTimeDropbox(false);
            }}
          />
        </div>
      </InputForm>
    </TimeContainer>
  );
};

//INFO: Time컴포넌트에서 사용되는 TimeDropdown 컴포넌트
const TimeDropdown = ({
  value,
  onChange,
  open,
  setOpen,
  minTime = { hour: 7, minutes: 0 },
  maxTime = { hour: 23, minutes: 0 },
}: {
  value: { hour: number; minutes: number };
  onChange?: (hour: number, minutes: number) => void;
  open: boolean;
  setOpen: (value: boolean) => void;
  minTime?: { hour: number; minutes: number };
  maxTime?: { hour: number; minutes: number };
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const Time = [
    { hour: 7, minutes: 0 },
    { hour: 7, minutes: 30 },
    { hour: 8, minutes: 0 },
    { hour: 8, minutes: 30 },
    { hour: 9, minutes: 0 },
    { hour: 9, minutes: 30 },
    { hour: 10, minutes: 0 },
    { hour: 10, minutes: 30 },
    { hour: 11, minutes: 0 },
    { hour: 11, minutes: 30 },
    { hour: 12, minutes: 0 },
    { hour: 12, minutes: 30 },
    { hour: 13, minutes: 0 },
    { hour: 13, minutes: 30 },
    { hour: 14, minutes: 0 },
    { hour: 14, minutes: 30 },
    { hour: 15, minutes: 0 },
    { hour: 15, minutes: 30 },
    { hour: 16, minutes: 0 },
    { hour: 16, minutes: 30 },
    { hour: 17, minutes: 0 },
    { hour: 17, minutes: 30 },
    { hour: 18, minutes: 0 },
    { hour: 18, minutes: 30 },
    { hour: 19, minutes: 0 },
    { hour: 19, minutes: 30 },
    { hour: 20, minutes: 0 },
    { hour: 20, minutes: 30 },
    { hour: 21, minutes: 0 },
    { hour: 21, minutes: 30 },
    { hour: 22, minutes: 0 },
    { hour: 22, minutes: 30 },
    { hour: 23, minutes: 0 },
  ];

  useEffect(() => {
    const handler = (event: any) => {
      if (open && ref.current && !ref.current.contains(event.target)) {
        setOpen?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  const filteredTime = Time.filter((item) => {
    if (item.hour < minTime.hour || (item.hour === minTime.hour && item.minutes < minTime.minutes)) {
      return false;
    }
    if (item.hour > maxTime.hour || (item.hour === maxTime.hour && item.minutes > maxTime.minutes)) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    if (open && ref.current) {
      const selectedIndex = filteredTime.findIndex(
        (time) => time.hour === value.hour && time.minutes === value.minutes
      );
      const itemHeight = 40;
      const scrollPosition = selectedIndex * itemHeight - (ref.current.clientHeight / 2 - itemHeight / 2);
      ref.current.scrollTop = scrollPosition;
    }
  }, [open, value]);

  return (
    <TimeDropdownContainer ref={ref} style={{ display: open ? "flex" : "none", width: "100%" }}>
      {Time.filter((item) => {
        if (minTime.hour === item.hour) {
          return minTime.minutes <= item.minutes;
        } else if (maxTime.hour === item.hour) {
          return maxTime.minutes >= item.minutes;
        } else {
          return minTime.hour < item.hour && maxTime.hour > item.hour;
        }
      }).map((time, index) => (
        <TimeDropdownItem key={`${time}-${index}`} onClick={() => onChange?.(time.hour, time.minutes)}>
          <UI.Typography
            type="body01 normal"
            fontStyle={value.hour === time.hour && value.minutes === time.minutes ? "bold" : "regular"}
            color="var(--alias-color-label-normal)"
          >
            {time.hour.toString().padStart(2, "0")}:{time.minutes.toString().padStart(2, "0")}
          </UI.Typography>
        </TimeDropdownItem>
      ))}
    </TimeDropdownContainer>
  );
};

//INFO: 회의실 예약 모달에서 회의실 선택으로 사용되는 컴포넌트
export const MeetingRoom = ({
  open,
  meetingRooms,
  onChange,
  value,
  date = new Date(),
  attendeeCount,
}: {
  open?: boolean;
  meetingRooms: TMeetingRoom[];
  onChange?: (value?: TMeetingRoom) => void;
  value?: TMeetingRoom;
  date?: Date;
  attendeeCount?: number;
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [showMeetingRooms, setShowMeetingRooms] = useState<TMeetingRoom[]>(meetingRooms);

  useEffect(() => {
    setShowMeetingRooms(meetingRooms);
  }, [meetingRooms]);

  return (
    <InputForm className="meeting-room">
      <Label>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
          회의실
        </UI.Typography>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-primary-normal)">
          *
        </UI.Typography>
      </Label>
      <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "8px" }}>
        <div style={{ position: "relative", width: "100%" }} onFocus={() => setModalOpen(true)}>
          <UI.Input
            onChange={debounce((value: string) => {
              if (value === "" || value === undefined) setShowMeetingRooms(meetingRooms);
              else
                setShowMeetingRooms(
                  meetingRooms.filter((item) => item.name.toLocaleLowerCase().includes(value.toLocaleLowerCase()))
                );
            }, 500)}
            placeholder="회의실 선택 (건물명, 회의실명 검색)"
            width={"100%"}
            prefixIcon="meetingRoom2"
            suffixIcon="search"
            showPrefixIcon
            showSuffixIcon
          />
          <MeetingRoomDropdown
            open={modalOpen}
            setOpen={setModalOpen}
            meetingRoomList={showMeetingRooms}
            onChange={(value) => {
              onChange?.(value);
              setModalOpen(false);
            }}
            value={value}
            attendeeCount={attendeeCount}
          />
        </div>
        {value && (
          <MeetingRoomDropdownItem
            onClick={() => {
              onChange?.(undefined);
            }}
          >
            <UI.Typography
              type="body02 normal"
              fontStyle={"regular"}
              color="var(--alias-color-label-normal)"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 308,
              }}
            >
              {value.name}
            </UI.Typography>
            <UI.Typography
              type="label01"
              fontStyle="medium"
              color="var(--alias-color-label-alternative)"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 308,
              }}
            >
              {value.building?.name ?? ""} - {value.floor?.name ?? ""} | {value.capacity}인
            </UI.Typography>
            <div style={{ position: "absolute", top: 21, right: 21 }}>
              <UI.Icon name="x" size={20} color="var(--alias-color-label-normal)" />
            </div>
          </MeetingRoomDropdownItem>
        )}
      </div>
    </InputForm>
  );
};

//INFO: MeetingRoom컴포넌트에서 사용되는 MeetingRoomDropdown 컴포넌트
const MeetingRoomDropdown = ({
  meetingRoomList,
  value,
  open,
  setOpen,
  onChange,
  attendeeCount,
}: {
  meetingRoomList?: TMeetingRoom[];
  value?: TMeetingRoom;
  open?: boolean;
  setOpen?: (value: boolean) => void;
  onChange?: (value?: TMeetingRoom) => void;
  attendeeCount?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [sortedMeetingRooms, setSortedMeetingRooms] = useState<TMeetingRoom[]>([]);

  const sortMeetingRoomsByCapacity = (meetingRooms: TMeetingRoom[], attendeeCount: number): TMeetingRoom[] => {
    return meetingRooms.sort((a, b) => {
      // 참석자 수보다 적은 경우를 뒤로 보내기 위한 조건 추가
      if (a.capacity < attendeeCount && b.capacity >= attendeeCount) {
        return 1; // a가 뒤로 가야 함
      }
      if (b.capacity < attendeeCount && a.capacity >= attendeeCount) {
        return -1; // b가 뒤로 가야 함
      }

      // 수용 인원이 충분한 경우, 차이를 기준으로 정렬
      const diffA = Math.abs(a.capacity - attendeeCount);
      const diffB = Math.abs(b.capacity - attendeeCount);

      return diffA - diffB;
    });
  };

  useEffect(() => {
    const updatedMeetingRooms = sortMeetingRoomsByCapacity(meetingRoomList ?? [], attendeeCount ?? 0);
    setSortedMeetingRooms(updatedMeetingRooms);
  }, [open, meetingRoomList]);

  useEffect(() => {
    const handler = (event: any) => {
      if (open && ref.current && !ref.current.contains(event.target)) {
        setOpen?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  return (
    <MeetingRoomDropdownContainer ref={ref} style={{ display: open ? "flex" : "none", width: "100%" }}>
      {sortedMeetingRooms?.map((item, index) => (
        <MeetingRoomDropdownItem key={`${item.id}-${index}`} onClick={() => onChange?.(item)}>
          <UI.Typography
            type="body02 normal"
            fontStyle={item === value ? "bold" : "regular"}
            color="var(--alias-color-label-normal)"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {item.name}
          </UI.Typography>
          <UI.Typography
            type="label01"
            fontStyle="medium"
            color="var(--alias-color-label-alternative)"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {item.building?.name ?? ""} - {item.floor?.name ?? ""} | {item.capacity}인
          </UI.Typography>
        </MeetingRoomDropdownItem>
      ))}
    </MeetingRoomDropdownContainer>
  );
};

//INFO: 회의실 예약 모달에서 참석자 추가로 사용되는 컴포넌트
export const Attendees = ({
  open,
  value,
  onChange,
  maxCapacity = 100,
  minAttendeeLimit = 1,
  isAllowViolateMinAttendeeLimit = false,
  onChangeAllowViolateMinAttendeeLimit,
}: {
  open?: boolean;
  value: TFormattedAttendee[];
  onChange?: (value: TFormattedAttendee[]) => void;
  maxCapacity?: number;
  minAttendeeLimit?: number;
  isAllowViolateMinAttendeeLimit?: boolean;
  onChangeAllowViolateMinAttendeeLimit?: () => void;
}) => {
  const [viewSearch, setViewSearch] = useState<string>();
  const [memberSearch, setMemberSearch] = useState<string>();
  const [memberList, setMemberList] = useState<TMember[]>([]);
  const [openAttendees, setOpenAttendees] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const [openMinAttendeeLimitWarning, setOpenMinAttendeeLimitWarning] = useState<boolean>(false);

  const debouncedChangeHandler = useCallback(
    _.debounce((value) => {
      setMemberSearch(value);
      if (value !== "") {
        setOpenAttendees(true);
      } else {
        setOpenAttendees(false);
      }
    }, 500),
    []
  );

  const onAddAttendee = (member?: TMember, email?: string, isVisitor?: boolean) => {
    if (isVisitor) {
      const isRegistered = value.some((item) => item.email === email);
      if (!isRegistered) {
        onChange?.([...value, { isVisitor: true, email: email ?? "" }]);
      }
    } else if (member) {
      const isRegistered = value.some((item) => item.email === member.email);
      if (!isRegistered) {
        onChange?.([
          ...value,
          { isVisitor: false, email: member.email, picture: member.image, id: member.id, name: member.nickname },
        ]);
      }
    }

    setViewSearch("");
    setMemberSearch(undefined);
    setOpenAttendees?.(false);
  };

  useEffect(() => {
    if (memberSearch) {
      getMemberList(1, undefined, memberSearch).then((res) => {
        setMemberList(res?.memberList ?? []);
      });
    } else {
      setMemberList([]);
    }
  }, [memberSearch]);

  useEffect(() => {
    const handler = (event: any) => {
      if (openAttendees && ref.current && !ref.current.contains(event.target)) {
        setOpenAttendees?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [openAttendees]);

  useEffect(() => {
    if (open) {
      setViewSearch("");
      setMemberSearch("");
      setMemberList([]);
    }
  }, [open]);

  return (
    <InputForm>
      <Label>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
          참석자
        </UI.Typography>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-primary-normal)">
          *
        </UI.Typography>
      </Label>
      <div
        style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%", position: "relative" }}
        onFocus={() => setOpenAttendees(true)}
      >
        <UI.Input
          value={viewSearch}
          helpText="참석자 수가 회의실 수용인원을 초과하였습니다."
          showHelpText={value.length > maxCapacity}
          placeholder="참석자 추가 (이름, 이메일 검색)"
          width={"100%"}
          prefixIcon="person"
          suffixIcon="search"
          showPrefixIcon
          showSuffixIcon
          onChange={(value) => {
            setViewSearch(value);
            debouncedChangeHandler(value);
          }}
          onEnter={() => {
            if (!memberSearch || !isValidEmail(memberSearch)) return;

            const addMember = memberList.find(
              (member) => member.nickname === memberSearch || member.email === memberSearch
            );
            if (addMember) {
              onAddAttendee(addMember, undefined, undefined);
            } else {
              onAddAttendee(undefined, memberSearch, true);
            }
            setViewSearch("");
            debouncedChangeHandler("");
          }}
        />
        {
          <MemberListContainer style={{ display: openAttendees ? "flex" : "none" }} ref={ref}>
            {memberList.length > 0 &&
              memberList.map((item, index) => {
                return (
                  <MemberItem
                    member={item}
                    key={`${item.id}-${index}`}
                    onClick={() => onAddAttendee(item, undefined, undefined)}
                  />
                );
              })}
            {memberSearch &&
              !memberList.map((item) => item.email).includes(memberSearch) &&
              !memberList.map((item) => item.nickname).includes(memberSearch) &&
              isValidEmail(memberSearch) && (
                <MemberListItem onClick={() => onAddAttendee(undefined, memberSearch, true)}>
                  <div className="empty-img">
                    <UI.Icon name="addPerson" size={16} color="var(--alias-color-label-assistive)" />
                  </div>
                  <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                    {memberSearch}
                  </UI.Typography>
                </MemberListItem>
              )}
          </MemberListContainer>
        }
        <div className="item-list" style={{ display: "flex", flexDirection: "column" }}>
          {value.map((item, index) => (
            <AttendeeItem
              key={`${item.id}-${index}`}
              value={item}
              onClick={() => {
                const deleted = value.filter((_, i) => i !== index);
                onChange?.(deleted);
              }}
            />
          ))}
        </div>
        {minAttendeeLimit > 1 && value.length < minAttendeeLimit && (
          <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "space-between" }}>
            <UI.Typography type="label02" fontStyle="regular" color="var(--alias-color-status-negative)">
              {isAllowViolateMinAttendeeLimit === false
                ? `해당 회의실, 시간대의 최소 참석 인원은 ${minAttendeeLimit}인 이상입니다.`
                : "본 예약은 최소 참석 인원 제한의 예외가 적용되었습니다."}
            </UI.Typography>
            {isAllowViolateMinAttendeeLimit === false && (
              <UI.ButtonGroups.Text
                type="secondary"
                title="예외 적용"
                onClick={() => setOpenMinAttendeeLimitWarning(true)}
                size="small"
                styles={{
                  text: {
                    textDecoration: "underline",
                    fontSize: "13px",
                  },
                }}
              />
            )}
          </div>
        )}
      </div>
      <MinAttendeeLimitWarning
        modalIsOpen={openMinAttendeeLimitWarning}
        closeModal={() => setOpenMinAttendeeLimitWarning(false)}
        onChange={onChangeAllowViolateMinAttendeeLimit}
      />
    </InputForm>
  );
};

const MemberItem = ({ member, onClick }: { member?: TMember; onClick?: () => void }) => {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <MemberListItem onClick={onClick}>
      {member?.image ? (
        <img src={member.image} />
      ) : (
        <div className="empty-img">
          <UI.Icon name="person" size={16} color="var(--alias-color-label-assistive)" />
        </div>
      )}
      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
        {member?.nickname}
      </UI.Typography>
      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
        |
      </UI.Typography>
      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-alternative)">
        {member?.email}
      </UI.Typography>
    </MemberListItem>
  );
};

//INFO: Attendees컴포넌트에서 사용되는 AttendeeItem 컴포넌트
export const AttendeeItem = ({ value, onClick }: { value: TFormattedAttendee; onClick?: () => void }) => {
  const userInfo = useRecoilValue(user);
  return (
    <AttendeeItemContainer onClick={onClick}>
      <div
        className="attendee-info"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flex: 1,
        }}
      >
        {!value.picture || value.picture === "" ? (
          <div
            className="empty-image"
            style={{
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: `var(--component-color-fill-alternative)`,
            }}
          >
            <UI.Icon name="person" size={20} color="var(--alias-color-label-assistive)" />
          </div>
        ) : (
          <img src={value.picture} width={28} height={28} style={{ borderRadius: "50%", objectFit: "cover" }} />
        )}
        <div className="info" style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {!value.isVisitor && (
            <>
              <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-normal)">
                {value.name}
                {userInfo?.id === value.id ? "(예약자)" : ""}
              </UI.Typography>
              <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-normal)">
                |
              </UI.Typography>
            </>
          )}
          <UI.Typography
            type="label01"
            fontStyle="medium"
            color={value.name ? "var(--alias-color-label-alternative)" : "var(--alias-color-label-normal)"}
          >
            {value.email} {value.isVisitor ? "*" : ""}
          </UI.Typography>
        </div>
      </div>
      <div className="delete">
        <UI.Icon name="x" size={20} />
      </div>
      <div className="interaction"></div>
    </AttendeeItemContainer>
  );
};

//INFO: 예약 메모 컴포넌트 구현
export const Memo = ({ onChange, value }: { onChange?: (value: string) => void; value: string }) => {
  return (
    <InputForm>
      <Label>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
          예약 메모
        </UI.Typography>
      </Label>
      <UI.Input
        multiline
        height={98}
        placeholder="예약 메모"
        width={"100%"}
        showMaxLength
        maxLength={1000}
        value={value}
        onChange={(value) => {
          if (value.length > 1000) return;
          else onChange?.(value);
        }}
      />
    </InputForm>
  );
};

//INFO: 비공개 회의 컴포넌트 구현
export const ReservationConfidential = ({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (value: boolean) => void;
}) => {
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }} onClick={() => onChange(!value)}>
      <UI.CheckBox selected={value} />
      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
        비공개 회의
      </UI.Typography>
      <ToolTip
        icon="info"
        text={"예약 제목, 예약자 및 참석자, 예약 메모를 비공개 처리합니다."}
        position={{
          top: -32,
          left: -100,
        }}
      />
    </div>
  );
};

//* INFO: 예약 일정 조정 권한 부여
export const EditPermissionCheckbox = ({ value, onChange }: { value: boolean; onChange: (value: boolean) => void }) => {
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }} onClick={() => onChange(!value)}>
      <UI.CheckBox selected={value} />
      <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
        예약 일정 조정 권한 부여
      </UI.Typography>
      <ToolTip
        icon="info"
        text={"예약 일정 수정 및 취소 권한을 참석자 모두에게 부여합니다."}
        position={{
          top: -32,
          left: -186,
        }}
      />
    </div>
  );
};

//* INFO: 반복 예약 버튼 컴포넌트
export const RepeatReservationBtn = ({
  selectedDate,
  onChange,
  isShow = true,
  editOption,
  minCount,
}: {
  selectedDate?: Date;
  onChange?: (rrule?: TRRule) => void;
  isShow?: boolean;
  editOption?: RepeatReservationConditionalOption;
  minCount?: number;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  return (
    <div style={{ display: isShow ? "flex" : "none" }}>
      <UI.Button
        type="outlined"
        title="반복"
        primary={false}
        size="large"
        style={{ whiteSpace: "nowrap" }}
        onClick={() => setOpen(true)}
      />
      <Modal.RepeatRuleSettingModal
        modalIsOpen={open}
        closeModal={() => setOpen(false)}
        selectedDate={selectedDate}
        minCount={minCount}
        editOption={editOption}
        onSetting={(rrule) => {
          onChange?.(rrule);
          setOpen(false);
        }}
      />
    </div>
  );
};

//* INFO: 반복 조건 컴포넌트
export const RepeatCondition = ({
  reservationDate,
  rrule,
  selectedDate = new Date(),
  onChange,
  editOption,
  minCount,
}: {
  rrule?: TRRule;
  selectedDate?: Date;
  onChange?: (rrule?: TRRule) => void;
  editOption?: RepeatReservationConditionalOption;
  minCount?: number;
  reservationDate?: Date;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const utilsService = new UtilsService();
  const rruleService = new RRuleService(utilsService);

  const getUnitText = () => {
    if (!rrule) return;
    const rruleString = rruleService.generateRRuleString(rrule, selectedDate);

    const unit = rruleService.transRRule(rruleString);
    return unit;
  };

  return (
    <InputForm style={{ display: rrule ? "flex" : "none", gap: 6 }}>
      <Label>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
          반복 조건
        </UI.Typography>
      </Label>
      <RepeatConditionContainer>
        <RepeatConditionItem>
          {editOption === RepeatReservationConditionalOption.THIS_SCHEDULE && reservationDate ? (
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
              {getKorDate(reservationDate.toString()).replaceAll(".", ". ")} 예약만 수정 중입니다.
            </UI.Typography>
          ) : (
            <>
              <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                {getUnitText()}
              </UI.Typography>
              <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                |
              </UI.Typography>
              <UI.Typography
                type="body01 normal"
                fontStyle="regular"
                color="var(--alias-color-label-normal)"
                style={{ display: "flex" }}
              >
                <p>
                  {!rrule?.endDate && !rrule?.repeatCount
                    ? "무기한 반복"
                    : rrule?.repeatEndOption === RepeatEndOption.TIMES
                      ? `${rrule.repeatCount}회 후`
                      : `${getKorDate(rrule?.endDate?.toString() ?? "")}`}
                </p>
                {(rrule?.endDate || rrule?.repeatCount) && <p style={{ marginLeft: 6 }}>반복 종료</p>}
              </UI.Typography>
            </>
          )}
        </RepeatConditionItem>
        {editOption !== RepeatReservationConditionalOption.THIS_SCHEDULE && (
          <div style={{ display: "flex", gap: 8 }}>
            <UI.Button type="outlined" title="수정" primary={false} size="small" onClick={() => setOpen(true)} />
            {!editOption && (
              <UI.Button
                type="outlined"
                title="삭제"
                primary={false}
                size="small"
                onClick={() => onChange?.(undefined)}
              />
            )}
          </div>
        )}
      </RepeatConditionContainer>
      <Modal.RepeatRuleSettingModal
        minCount={minCount}
        editOption={editOption}
        selectedRrule={rrule}
        modalIsOpen={open}
        closeModal={() => setOpen(false)}
        selectedDate={selectedDate}
        onSetting={(rrule) => {
          onChange?.(rrule);
          setOpen(false);
        }}
      />
    </InputForm>
  );
};
